import { useTranslation } from 'react-i18next';

import { classDef, PRODUCT_CLASS_ORDER, productClassesTotal } from '../utils/product-groups';

interface ProductClassesBreakdownProps {
  /** Products per class the batch takes (or took). */
  included: Record<string, unknown> | null | undefined;
  /** Products per class the create screen offered; adds an "Offered" column when present. */
  offered?: Record<string, unknown> | null;
  /** Products carried in from earlier batches ("tried before, not finished"). */
  carriedIn?: number | null;
  /**
   * The difference to the batch's product count the split does not explain (negative = dropped
   * before enrichment: validation, the product cap, a restart). Shown as its own row so the total
   * is the count the cost was estimated on.
   */
  adjustment?: number | null;
  testId?: string;
  className?: string;
}

const asCount = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/**
 * A batch's products by group — the sync's classes and those carried in — as a small table.
 * Counts only: shown at cost approval and on the batch page alike. Renders nothing when the
 * batch recorded no split (an older batch, or a curation that sent none).
 */
export function ProductClassesBreakdown({
  included,
  offered = null,
  carriedIn = null,
  adjustment = null,
  testId = 'product-classes-breakdown',
  className,
}: ProductClassesBreakdownProps) {
  const { t } = useTranslation('content-enrichment');
  const rows = PRODUCT_CLASS_ORDER.map((id) => {
    const taken = asCount(included?.[id]);
    return {
      id,
      def: classDef(id),
      included: taken,
      // Already-curated products are never offered to a net_new batch (only an `all` run takes
      // them), so they count as offered only where the batch actually took some.
      offered: id === 'curated' && !(taken !== null && taken > 0) ? null : asCount(offered?.[id]),
    };
  }).filter((r) => (r.included ?? 0) > 0 || (r.offered ?? 0) > 0);
  const carried = asCount(carriedIn) ?? 0;
  if (rows.length === 0 && carried === 0) return null;
  const showOffered = offered !== null;
  // Unknown until curation has counted what the batch took: "—", never a misleading 0.
  const known = rows.some((r) => r.included !== null);
  const adjust = asCount(adjustment) ?? 0;
  const total = productClassesTotal(included, carriedIn, adjustment);

  return (
    <div data-testid={testId} className={`overflow-x-auto ${className ?? ''}`}>
      <table className="w-full min-w-[280px] border-collapse text-sm">
        <thead>
          <tr className="text-muted-foreground text-left text-xs">
            <th scope="col" className="py-1.5 font-medium">
              {t('productGroups.summary.group')}
            </th>
            {showOffered && (
              <th scope="col" className="py-1.5 text-right font-medium">
                {t('productGroups.breakdown.offered')}
              </th>
            )}
            <th scope="col" className="py-1.5 text-right font-medium">
              {t('productGroups.breakdown.included')}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} data-testid={`${testId}-row-${r.id}`} className="border-t">
              <td className="py-2">
                <span
                  className="inline-flex items-center gap-2"
                  title={r.def ? t(`productGroups.parts.${r.def.labelKey}.description`) : undefined}
                >
                  <span
                    aria-hidden="true"
                    className="h-[9px] w-[9px] rounded-[3px]"
                    style={{ background: r.def?.color ?? '#8A93A6' }}
                  />
                  {r.def ? t(`productGroups.parts.${r.def.labelKey}.label`) : r.id}
                </span>
              </td>
              {showOffered && (
                <td className="py-2 text-right tabular-nums">
                  {r.offered === null ? '—' : r.offered.toLocaleString()}
                </td>
              )}
              <td className="py-2 text-right tabular-nums">
                {r.included === null ? '—' : r.included.toLocaleString()}
              </td>
            </tr>
          ))}
          {carried > 0 && (
            <tr data-testid={`${testId}-row-carried`} className="border-t">
              <td className="py-2">{t('productGroups.breakdown.carriedIn')}</td>
              {showOffered && <td className="py-2 text-right">—</td>}
              <td className="py-2 text-right tabular-nums">{carried.toLocaleString()}</td>
            </tr>
          )}
          {known && adjust !== 0 && (
            <tr data-testid={`${testId}-row-adjustment`} className="border-t">
              <td className="text-muted-foreground py-2">
                {t(
                  adjust < 0
                    ? 'productGroups.breakdown.droppedBefore'
                    : 'productGroups.breakdown.addedAfter',
                )}
              </td>
              {showOffered && <td className="py-2 text-right">—</td>}
              <td className="text-muted-foreground py-2 text-right tabular-nums">
                {adjust > 0 ? '+' : '−'}
                {Math.abs(adjust).toLocaleString()}
              </td>
            </tr>
          )}
          <tr className="border-t-2 font-semibold">
            <td className="py-2">{t('productGroups.summary.total')}</td>
            {showOffered && <td />}
            <td className="py-2 text-right tabular-nums" data-testid={`${testId}-total`}>
              {total === null ? '—' : total.toLocaleString()}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
